import BaseVueComponent from "../../../_imports/shared/base-vue-component.vue.common";
import FormsFieldErrorMessage from "../../messages/forms-field-error-message.vue";
import FormsFieldLabel from "../../labels/forms-field-label.vue";
import formStore from '../../_imports/store/form';

class Validity {
    constructor(success, message = null) {
        this.success = success;
        this.message = message;
    }
}

export default {
    mixins: [
        BaseVueComponent
    ],
    components: {
        FormsFieldErrorMessage,
        FormsFieldLabel
    },
    props: {
        field: Object,
        isInnerField: Boolean
    },
    data() {
        return {
            baseClasses: [],
            connected: true,
            containerBaseClasses: [],
            error: null,
            focused: false,
            ignore: false,
            inputBaseClasses: [],
            isDisabled: false,
            isEnriched: false,
            isValid: false,
            isVisible: true,
            state: {},
            userInput: null,
            validations_: []
        }
    },
    inject: [
        'getForm',
        'getFormEvent'
    ],
    created() {
        this.registerField_();
        this.setInitialState_();
        this.handleFormEvents_();
        this.addBaseValidations_();

        if (this.field?.fieldDependency?.fieldId) {
            this.isVisible = false;
        }

        if (this.onCreated) {
            this.onCreated();
        }
    },
    computed: {
        fieldBaseClasses() {
            return this.baseClasses.join(' ');
        },
        fieldContainerBaseClasses() {
            return this.containerBaseClasses.join(' ');
        },
        fieldInputBaseClasses() {
            return this.inputBaseClasses.join(' ');
        },
        form() {
            return this.getForm();
        },
        formEvent() {
            return this.getFormEvent();
        },
        formState() {
            return formStore.formState;
        },
        labelsAboveFields() {
            return this.form.data.labelsAboveFields;
        }
    },
    watch: {
        connected() {
            this.updateState(state => state.connected = this.connected);
        },
        error() {
            this.updateFieldClasses('has-error', this.error);
        },
        isDisabled() {
            this.updateFieldContainerClasses('is-disabled', this.isDisabled);
            this.updateFieldInputClasses('is-disabled', this.isDisabled);
        },
        isEnriched() {
            this.updateState(state => state.isEnriched = this.isEnriched);
        },
        focused() {
            this.updateFieldInputClasses('focused', this.focused);
        },
        formState() {
            this.isDisabled = this.formState.formVerifyingEmail || this.formState.formIsSubmitting;
        },
        ignore() {
            this.updateState(state => state.ignore = this.ignore);
        },
        labelsAboveFields: {
            handler() {
                this.updateFieldInputClasses('label-above', this.labelsAboveFields);
            },
            immediate: true
        },
        isVisible() {
            this.updateState(state => state.isVisible = this.isVisible);
        },
        state() {
            this.sendStateUpdate_();
        },
        userInput() {
            this.updateFieldContainerClasses('has-value', this.userInput !== null && this.userInput !== '');
        }
    },
    methods: {
        // "Private" methods, not to be used outside this file
        updateBaseClasses_(classesSource, baseClass, baseCondition) {
            if (baseCondition
                && !this[classesSource].includes(baseClass)) {
                this[classesSource].push(baseClass);
            } else if (!baseCondition) {
                this[classesSource] = this[classesSource].filter(classSource => classSource !== baseClass);
            }
        },
        addBaseValidations_() {
            this.addFieldValidation(validation => {
                if (validation.required
                    && (!this.userInput || (typeof this.userInput !== 'boolean' && !this.userInput.length))) {
                    return validation.hasFailed(this.getFieldErrorMessage('fieldRequiredError'));
                }

                return validation.hasPassed();
            });
        },
        eventIsPrevented_(event) {
            return event.defaultPrevented;
        },
        getFieldValidity_() {
            const validation = this.validations_.find(v => !v().success);

            if (validation) {
                return validation();
            }

            return new Validity(true);
        },
        handleFormEvents_() {
            this.formEvent.on('validatefields', this.validateField);

            this.formEvent.on('blurfields', event => {
                if (this.field) {
                    if (!event.exceptions?.includes(this.field.name)) {
                        this.focused = false;
                    }
                }
            });

            this.formEvent.on('connectfields', () => this.connected = true);

            this.formEvent.on('disconnectfields', ({ fields }) => {
                if (this.field) {
                    this.connected = !fields.some(field => field === this.field.name);
                }
            });

            this.formEvent.on('enrichfield', field => {
                if (field?.name?.toLowerCase() === this.field?.name?.toLowerCase()
                    && this.connected) {
                    this.userInput = field.value;
                    this.isEnriched = true;
                }
            });

            this.formEvent.on('unenrichfields', () => {
                if (this.isEnriched) {
                    this.isEnriched = false;
                    this.isVisible = true;
                    this.userInput = null;
                }
            });

            this.formEvent.on('hidefield', field => {
                if (this.field?.name === field?.name) {
                    this.isVisible = false;
                }
            });

            this.formEvent.on('updatefieldvalue', event => {
                if (this.field?.name?.toLowerCase() === event.name?.toLowerCase()) {
                    this.userInput = event.value;
                }
            });

            CoveoWebsite?.eventQueue?.forEach(event => {
                if (event.name === 'updatefieldvalue') {
                    if (this.field?.name?.toLowerCase() === event.detail.name?.toLowerCase()) {
                        this.userInput = event.detail.value;
                    }
                }
            });

            this.formEvent.on('fieldvalueupdated', field => {
                if (this.field?.fieldDependency?.fieldId
                    && field.id === this.field.fieldDependency.fieldId) {
                    const valueDependencies = this.field.fieldDependency.value.split(';');

                    this.isVisible = valueDependencies.includes(field.value);
                    this.ignore = !this.isVisible;

                    this.updateState(state => state.isVisible = this.isVisible);
                }
            });
        },
        isLabelActive_() {
            return this.userInput !== '' && this.userInput !== null;
        },
        onBlur_() {
            this.focused = false;

            const fieldValidity = this.getFieldValidity_();
            this.error = fieldValidity.message;
        },
        onFocus_() {
            this.focused = true;

            this.$root.$emit('focusfield', this.field);
        },
        sendStateUpdate_() {
            this.$emit('state-update', this.state);
            this.$emit('inner-field-update', this);

            if (!this.isInnerField) {
                this.$root.$emit('state-update', this.state);
            }
        },
        setInitialState_() {
            this.updateState(state => {
                if (this.field) {
                    state.fieldId = this.field.fieldId;
                    state.name = this.state.name || this.field.name;
                    state.connected = this.connected;
                }

                state.isValid = this.isValid;
            });
        },
        registerField_() {
            this.$emit('register-field', this.field);
            this.$root.$emit('register-field', this.field);
        },
        updateState_(stateModifier) {
            const newState = {}
            stateModifier(newState);

            const state = {
                ...this.state,
                ...newState
            };

            this.state = state;
        },

        // "Public" methods, can be used outside this file

        addFieldValidation(fieldValidation) {
            if (!this.field
                || !this.field.validation) {
                return;
            }

            const validator = Object.create(this.field.validation);

            validator.hasPassed = () => {
                return new Validity(true);
            };

            validator.hasFailed = message => {
                return new Validity(false, message);
            };

            this.validations_.push(() => fieldValidation(validator));
        },
        getData(variableName) {
            if (this[variableName]) {
                return this[variableName];
            } else if (this.field) {
                return this.field[variableName];
            }
        },
        getFieldErrorMessage(errorMessage) {
            if (this.field
                && this.field.overrideFieldErrorMessages
                && this.field.overrideFieldErrorMessages[errorMessage]) {
                return this.field.overrideFieldErrorMessages[errorMessage];
            }

            let fieldErrorMessage = 'Invalid field value.';

            if (this.form.fieldErrorMessages
                && this.form.fieldErrorMessages[errorMessage]) {
                fieldErrorMessage = this.form.fieldErrorMessages[errorMessage];
            }

            return fieldErrorMessage;
        },
        getRandomId(prepend, length = 5) {
            const random = Math.random().toString(36).slice(2);

            return `${prepend}${random}`;
        },
        hasValidationRequired() {
            return this.field && this.field.validation && this.field.validation.required;
        },
        setUserInput(userInput) {
            this.userInput = userInput;
        },
        updateFieldClasses(baseClass, baseCondition) {
            this.updateBaseClasses_('baseClasses', baseClass, baseCondition);
        },
        updateFieldContainerClasses(baseClass, baseCondition) {
            this.updateBaseClasses_('containerBaseClasses', baseClass, baseCondition);
        },
        updateFieldInputClasses(baseClass, baseCondition) {
            this.updateBaseClasses_('inputBaseClasses', baseClass, baseCondition);
        },
        updateState(stateModifier) {
            this.updateState_(stateModifier);
        },
        validateField() {
            let isValid = true;

            if (this.field
                && this.field.validation) {
                const fieldValidity = this.getFieldValidity_();
                isValid = fieldValidity ? fieldValidity.success : isValid;

                this.error = !isValid ? fieldValidity.message : null;
            } else {
                this.error = null;
            }

            this.updateState_(state => {
                state.isValid = isValid;
                state.userInput = this.userInput;
            });
        }
    }
};